import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useSEO, buildBreadcrumbSchema } from "../hooks/useSEO";
import { AdSlot } from "../components/AdSlot";
import { taxAPI } from "../lib/api";
import { toast } from "sonner";

/**
 * States Hub — /states
 * SEO landing page listing all 51 jurisdictions (50 states + DC) with
 * their 2026 top marginal rates and links to per-state calculators.
 */
const StatesHub = () => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useSEO({
    title: "2026 State Income Tax Calculators · All 50 States",
    description:
      "Federal + state 2026 tax calculators for every state. See how OBBBA interacts with California, Texas, New York, Florida and every state's 2026 rates.",
    keywords:
      "state income tax calculator 2026, california tax calculator 2026, texas 2026 taxes, new york state tax calculator, obbba state tax conformity",
    path: "/states",
    jsonLd: buildBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "State Calculators", path: "/states" },
    ]),
  });

  useEffect(() => {
    taxAPI
      .states()
      .then((d) => setStates(d.states || []))
      .catch(() => toast.error("Could not load states"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = states.filter((s) =>
    s.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const noTax = filtered.filter((s) => s.type === "none");
  const flat = filtered.filter((s) => s.type === "flat");
  const progressive = filtered.filter((s) => s.type === "progressive");

  const group = (label, list, testid) => (
    <div className="mb-12">
      <div className="flex items-baseline justify-between border-b-2 border-foreground pb-3 mb-6">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold">{label}</h2>
        <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          {list.length} {list.length === 1 ? "state" : "states"}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-0 border-t border-l border-foreground/20" data-testid={testid}>
        {list.map((s) => (
          <Link
            key={s.slug}
            to={`/calculators/state-income-tax/${s.slug}`}
            className="group p-5 border-r border-b border-foreground/20 bg-card hover:bg-foreground hover:text-background transition-colors"
            data-testid={`state-card-${s.slug}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground group-hover:text-background/60">
                {s.abbr}
              </span>
              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="font-heading text-lg font-semibold leading-tight mb-1">
              {s.name}
            </h3>
            <p className="font-mono text-[11px] uppercase tracking-wider text-foreground/70 group-hover:text-background/70">
              {s.summary}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <section className="border-b-2 border-foreground py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono uppercase text-[10px] tracking-[0.3em] text-muted-foreground mb-4 block">
            The State Layer
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
            data-testid="states-hub-title"
          >
            Your <span className="italic">state</span> matters.
          </h1>
          <p className="font-body text-lg text-foreground/80 max-w-3xl leading-relaxed">
            OBBBA rewrote federal tax law for 2026 — but your total bill depends
            on where you live. We built a federal + state combined calculator
            for all 50 states and DC, with 2026 brackets and each state's OBBBA
            conformity notes.
          </p>
          <div className="mt-10 max-w-md">
            <label htmlFor="state-search" className="sr-only">
              Search states
            </label>
            <input
              id="state-search"
              type="search"
              placeholder="Search by state name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full px-4 py-3 font-mono text-base border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
              data-testid="state-search-input"
            />
          </div>
        </div>
      </section>

      <AdSlot size="leaderboard" testid="states-hub-ad" />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center gap-3 py-16 font-mono text-sm uppercase tracking-widest text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading states...
            </div>
          ) : (
            <>
              {noTax.length > 0 && group("No state income tax", noTax, "group-none")}
              {flat.length > 0 && group("Flat-rate states", flat, "group-flat")}
              {progressive.length > 0 &&
                group("Progressive-rate states", progressive, "group-progressive")}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default StatesHub;
